import type { CreateIdeaStopFormData } from './ideaStop.types';

interface IdeaStopFieldConfig {
  mandatory: string[];
  optional: string[];
}

export const IDEA_STOP_FIELD_TYPES = {
  flightFrom: 'airport',
  flightTo: 'airport',

  countryFrom: 'country',
  countryTo: 'country',

  airline: 'airline',

  address: 'address',
  departureAddress: 'address',
  arrivalAddress: 'address',
  startingPortAddress: 'address',
  endingPortAddress: 'address',
} as const;

export const IDEA_STOP_UI_TO_API_FIELD_MAP: Record<string, string> = {
  departureDate: 'departure_date_time',
  departureTime: 'departure_date_time',
  arrivalDate: 'arrival_date_time',
  arrivalTime: 'arrival_date_time',
  startDate: 'start_date_time',
  startTime: 'start_date_time',
  endDate: 'end_date_time',
  endTime: 'end_date_time',
  checkInDate: 'check_in_date_time',
  checkInTime: 'check_in_date_time',
  checkOutDate: 'check_out_date_time',
  checkOutTime: 'check_out_date_time',

  flightFrom: 'flight_from',
  flightTo: 'flight_to',
  flightNumber: 'flight_number',
  airline: 'airline_iata_code',

  vehicleType: 'vehicle_type',
  transportName: 'transport_name',
  shipName: 'transport_name',

  departureLocation: 'location_from_name',
  arrivalLocation: 'location_to_name',
  startingPortName: 'location_from_name',
  endingPortName: 'location_to_name',
  departureStation: 'location_from_name',
  arrivalStation: 'location_to_name',

  countryFrom: 'country_from',
  countryTo: 'country_to',
  borderCheckpoint: 'border_checkpoint',

  departureAddress: 'departure_address',
  arrivalAddress: 'arrival_address',
  startingPortAddress: 'starting_port_address',
  endingPortAddress: 'ending_port_address',
};

export const IDEA_STOP_FIELD_CONFIG: Record<string, IdeaStopFieldConfig> = {
  Flight: {
    mandatory: ['ideaStopType', 'description', 'flightFrom', 'flightTo'],
    optional: [
      'departureDate',
      'departureTime',
      'arrivalDate',
      'arrivalTime',
      'airline',
      'flightNumber',
      'notes',
      'email',
      'phone',
      'link',
    ],
  },

  Hotel: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: [
      'checkInDate',
      'checkInTime',
      'checkOutDate',
      'checkOutTime',
      'address',
      'phone',
      'email',
      'link',
      'notes',
    ],
  },

  Activity: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: ['startDate', 'startTime', 'endDate', 'endTime', 'place', 'address', 'email', 'phone', 'link', 'notes'],
  },

  CarRental: {
    mandatory: ['ideaStopType', 'description'],
    optional: [
      'startDate',
      'startTime',
      'endDate',
      'endTime',
      'address',
      'transportName',
      'email',
      'phone',
      'link',
      'notes',
    ],
  },

  Concert: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: ['startDate', 'startTime', 'endDate', 'endTime', 'place', 'address', 'email', 'phone', 'link', 'notes'],
  },

  Theatre: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: ['startDate', 'startTime', 'endDate', 'endTime', 'place', 'address', 'email', 'phone', 'link', 'notes'],
  },

  Meeting: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: ['startDate', 'startTime', 'endDate', 'endTime', 'place', 'address', 'email', 'phone', 'link', 'notes'],
  },

  Shopping: {
    mandatory: ['ideaStopType', 'description'],
    optional: [
      'name',
      'startDate',
      'startTime',
      'endDate',
      'endTime',
      'place',
      'address',
      'email',
      'phone',
      'link',
      'notes',
    ],
  },

  Restaurant: {
    mandatory: ['ideaStopType', 'description'],
    optional: [
      'name',
      'startDate',
      'startTime',
      'endDate',
      'endTime',
      'place',
      'address',
      'email',
      'phone',
      'link',
      'notes',
    ],
  },

  Tour: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: ['startDate', 'startTime', 'endDate', 'endTime', 'place', 'address', 'email', 'phone', 'link', 'notes'],
  },

  Parking: {
    mandatory: ['ideaStopType', 'description', 'place'],
    optional: ['startDate', 'startTime', 'endDate', 'endTime', 'address', 'phone', 'email', 'link', 'notes'],
  },

  Directions: {
    mandatory: ['ideaStopType', 'description', 'place'],
    optional: ['startDate', 'startTime', 'endDate', 'endTime', 'address', 'phone', 'email', 'link', 'notes'],
  },

  BorderCrossing: {
    mandatory: ['ideaStopType', 'description', 'countryFrom', 'countryTo'],
    optional: ['borderCheckpoint', 'address', 'email', 'phone', 'link', 'notes'],
  },

  Ferry: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: [
      'departureDate',
      'departureTime',
      'arrivalDate',
      'arrivalTime',
      'departureLocation',
      'departureAddress',
      'arrivalLocation',
      'arrivalAddress',
      'email',
      'phone',
      'link',
      'notes',
    ],
  },

  Cruise: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: [
      'departureDate',
      'departureTime',
      'arrivalDate',
      'arrivalTime',
      'shipName',
      'startingPortName',
      'startingPortAddress',
      'endingPortName',
      'endingPortAddress',
      'email',
      'phone',
      'link',
      'notes',
    ],
  },

  Rail: {
    mandatory: ['ideaStopType', 'description', 'name'],
    optional: [
      'departureDate',
      'departureTime',
      'arrivalDate',
      'arrivalTime',
      'departureStation',
      'departureAddress',
      'arrivalStation',
      'arrivalAddress',
      'email',
      'phone',
      'link',
      'notes',
    ],
  },

  Transportation: {
    mandatory: ['ideaStopType', 'description', 'vehicleType'],
    optional: [
      'departureDate',
      'departureTime',
      'arrivalDate',
      'arrivalTime',
      'departureLocation',
      'departureAddress',
      'arrivalLocation',
      'arrivalAddress',
      'email',
      'phone',
      'link',
      'notes',
    ],
  },
};

export const getFieldsForIdeaStop = (ideaStopType: string) => {
  return IDEA_STOP_FIELD_CONFIG[ideaStopType] || { mandatory: [], optional: [] };
};

export const shouldShowIdeaStopField = (fieldName: string, ideaStopType: string) => {
  if (!ideaStopType) return fieldName === 'ideaStopType';

  const config = IDEA_STOP_FIELD_CONFIG[ideaStopType];
  if (!config) return false;

  return config.mandatory.includes(fieldName) || config.optional.includes(fieldName);
};

export const getIdeaStopFieldInputType = (
  fieldName: string
): 'airport' | 'country' | 'address' | 'airline' | 'default' => {
  return IDEA_STOP_FIELD_TYPES[fieldName as keyof typeof IDEA_STOP_FIELD_TYPES] || 'default';
};

export const getIdeaStopApiFieldName = (uiFieldName: string): string => {
  return IDEA_STOP_UI_TO_API_FIELD_MAP[uiFieldName] || uiFieldName;
};

/** Map API validation `attr` (snake_case) back to CreateIdeaStopFormData field name (camelCase). */
export const getIdeaStopUiFieldFromApiAttr = (apiAttr: string): string | undefined => {
  const mapped = (Object.entries(IDEA_STOP_UI_TO_API_FIELD_MAP) as [string, string][]).find(
    ([, apiName]) => apiName === apiAttr
  );
  if (mapped) {
    return mapped[0];
  }
  if (!apiAttr.includes('_')) {
    return undefined;
  }
  return apiAttr.replace(/_([a-z])/g, (_, ch: string) => ch.toUpperCase());
};

export const isIdeaStopFieldMandatory = (fieldName: string, ideaStopType: string): boolean => {
  const config = IDEA_STOP_FIELD_CONFIG[ideaStopType];
  if (!config) return false;
  return config.mandatory.includes(fieldName);
};

const hasAirportOrAirlineSelection = (
  values: CreateIdeaStopFormData,
  fieldName: string
): boolean => {
  const value = values[fieldName as keyof CreateIdeaStopFormData];
  if (typeof value === 'string' && value.trim() !== '') return true;

  const detailsKey = `${fieldName}_details` as keyof CreateIdeaStopFormData;
  const details = values[detailsKey];

  if (details && typeof details === 'object' && 'iata_code' in details) {
    const code = (details as { iata_code?: string }).iata_code;
    return Boolean(code?.trim());
  }

  return false;
};

export const isIdeaStopMandatoryFieldValueFilled = (
  values: CreateIdeaStopFormData,
  fieldName: string
): boolean => {
  const inputType = getIdeaStopFieldInputType(fieldName);

  if (inputType === 'airport' || inputType === 'airline') {
    return hasAirportOrAirlineSelection(values, fieldName);
  }

  const value = values[fieldName as keyof CreateIdeaStopFormData];
  if (typeof value !== 'string') return false;

  return value.trim() !== '';
};

export const areIdeaStopMandatoryFieldsFilled = (
  values: CreateIdeaStopFormData,
  ideaStopType: string
): boolean => {
  if (!ideaStopType?.trim()) return false;

  const { mandatory } = getFieldsForIdeaStop(ideaStopType);

  return mandatory.every(fieldName => {
    if (fieldName === 'ideaStopType') return true;

    return isIdeaStopMandatoryFieldValueFilled(values, fieldName);
  });
};
